"use client";
import { useCallback, useEffect, useState } from "react";

/** False during SSR and on the first client render, so hydration matches. */
const usePrefersReducedMotion = (): boolean => {
  const [prefersReduced, setPrefersReduced] = useState(false);

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReduced(query.matches);

    const onChange = (event: MediaQueryListEvent) => {
      setPrefersReduced(event.matches);
    };
    query.addEventListener("change", onChange);
    return () => {
      query.removeEventListener("change", onChange);
    };
  }, []);

  return prefersReduced;
};

interface SlideshowOptions {
  /** Number of slides. 0 or 1 means the slideshow is inert. */
  count: number;
  intervalMs: number;
  /** Caller-specific pause conditions (hover, an expanded card, wrong layout). */
  paused?: boolean;
}

interface Slideshow {
  index: number;
  next: () => void;
  prev: () => void;
  goTo: (index: number) => void;
  prefersReducedMotion: boolean;
  /** Whether auto-advance is actually running right now. */
  playing: boolean;
  /** Explicit play/pause. Resumes even after manual navigation stopped it. */
  setPlaying: (next: boolean) => void;
}

/**
 * The rotation state machine every auto-advancing slideshow shares: interval
 * timer, wrap-around index, manual navigation, and reduced-motion handling.
 * Rendering stays with the caller — a crossfade stack and a sliding track are
 * different presentations of this same state.
 *
 * Reduced motion stops auto-advance; manual navigation still works. Manual
 * navigation also ends auto-advance for good — see `tookControl`.
 */
const useSlideshow = ({ count, intervalMs, paused = false }: SlideshowOptions): Slideshow => {
  const [index, setIndex] = useState(0),
    // Explicit navigation stops the timer: a visitor who picks a slide is not
    // Asking to be moved off it three seconds later. A play control clears it.
    [autoplayOff, setAutoplayOff] = useState(false),
    prefersReducedMotion = usePrefersReducedMotion(),
    rotates = count > 1 && !paused && !autoplayOff && !prefersReducedMotion;

  useEffect(() => {
    if (!rotates) {
      return;
    }

    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % count);
    }, intervalMs);
    return () => {
      window.clearInterval(timer);
    };
  }, [rotates, count, intervalMs]);

  // If the slide list shrinks under the current position (e.g. fetched reviews
  // Replacing placeholders), snap back to the first slide instead of pointing
  // At a slide that no longer exists.
  useEffect(() => {
    setIndex((current) => {
      if (count > 0 && current >= count) {
        return 0;
      }
      return current;
    });
  }, [count]);

  const goTo = useCallback(
      (target: number) => {
        setAutoplayOff(true);
        setIndex((current) => {
          if (count > 0) {
            return ((target % count) + count) % count;
          }
          return current;
        });
      },
      [count],
    ),

    next = useCallback(() => {
      setAutoplayOff(true);
      setIndex((current) => {
        if (count > 0) {
          return (current + 1) % count;
        }
        return current;
      });
    }, [count]),

    prev = useCallback(() => {
      setAutoplayOff(true);
      setIndex((current) => {
        if (count > 0) {
          return (current - 1 + count) % count;
        }
        return current;
      });
    }, [count]),

    setPlaying = useCallback((next: boolean) => {
      setAutoplayOff(!next);
    }, []);

  return { goTo, index, next, playing: rotates, prefersReducedMotion, prev, setPlaying };
};

export type { SlideshowOptions, Slideshow };
export { usePrefersReducedMotion, useSlideshow };
